import { Form } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useLazyGetBranchesForSystemQuery,
  useGetBranchesForSystemQuery,
} from '@app/api/Branches';
import { U_ACTION } from '@app/constants';
import {
  useNewUserAccessRights,
  usePagination,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { WAREHOUSE_REGISTRY_COLUMNS } from '@app/pages/Warehouses/WarehouseRegistry/data';
import type { FiltersForm } from '@app/pages/Warehouses/WarehouseRegistry/types';
import { adaptWareHousesToTable } from '@app/pages/Warehouses/WarehouseRegistry/utils';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import { useSearchWarehousesMutation } from '@app/services/rtkQuery/Warehouses/api';
import type {
  ModelWarehouseFilter,
  ModelWarehousesSearchResult,
} from '@app/services/rtkQuery/Warehouses/types';
import { useErrorHandler } from '@app/shared/hooks';

import type { BaseOptionType } from 'antd/es/select';

const { useForm } = Form;

const STORAGE_KEY = 'warehouse-registry-filters';

type StoredWarehouseRegistryFilters = {
  SearchTerm: string | null;
  BranchIds: number[] | null;
  Page: number;
  ItemsPerPage: number;
};

const DEFAULT_PAGE = 1;
const DEFAULT_ITEMS_PER_PAGE = 20;

const getStoredFilters = (): StoredWarehouseRegistryFilters | null => {
  try {
    return safeParseStorage(sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
};

const safeParseStorage = (
  raw: string | null
): StoredWarehouseRegistryFilters | null => {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredWarehouseRegistryFilters>;

    const page =
      typeof parsed.Page === 'number' && parsed.Page > 0
        ? parsed.Page
        : DEFAULT_PAGE;

    const itemsPerPage =
      typeof parsed.ItemsPerPage === 'number' && parsed.ItemsPerPage > 0
        ? parsed.ItemsPerPage
        : DEFAULT_ITEMS_PER_PAGE;

    const searchTerm =
      typeof parsed.SearchTerm === 'string'
        ? parsed.SearchTerm
        : parsed.SearchTerm === null
        ? null
        : null;

    const branchIds =
      Array.isArray(parsed.BranchIds) &&
      parsed.BranchIds.every((x) => typeof x === 'number')
        ? parsed.BranchIds
        : parsed.BranchIds === null
        ? null
        : null;

    return {
      SearchTerm: searchTerm,
      BranchIds: branchIds,
      Page: page,
      ItemsPerPage: itemsPerPage,
    };
  } catch {
    return null;
  }
};

const writeStorage = (value: StoredWarehouseRegistryFilters) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    return;
  }
};

const clearStorage = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    return;
  }
};

export const useWarehousesRegistry = () => {
  const { activeBranchId } = useUserPrint();
  const { isMobile } = useWindowSize();

  const [selectedBranches, setSelectedBranches] = useState<BaseOptionType[]>(
    []
  );
  const [allBranches, setAllBranches] = useState<BaseOptionType[]>([]);
  const [warehousesData, setWarehousesData] =
    useState<ModelWarehousesSearchResult | null>(null);

  const { showNotification } = useNotification();

  const [searchWarehouses, { isLoading: isSearchWarehousesLoading }] =
    useSearchWarehousesMutation();
  const [getBranchesForSystem] = useLazyGetBranchesForSystemQuery();

  const {
    userAccessRights: {
      Warehouse: { IsWarehouseEdit, IsOn },
    },
  } = useNewUserAccessRights();

  const isPageForbidden = useMemo(() => !IsOn, [IsOn]);

  const {
    data: branches,
    isLoading: isBranchesLoading,
    isFetching: isBranchesFetching,
    error: branchesError,
  } = useGetBranchesForSystemQuery(
    { ActiveBranchId: activeBranchId },
    { skip: !activeBranchId || !IsOn }
  );

  useErrorHandler([{ flag: !!branchesError, error: branchesError }]);

  const [filtersForm] = useForm<FiltersForm>();
  const storedFilters = useMemo(() => getStoredFilters(), []);
  const initialFilterPayload = useMemo<ModelWarehouseFilter>(() => {
    return {
      SearchTerm: storedFilters?.SearchTerm ?? null,
      BranchIds: storedFilters?.BranchIds ?? null,
      ItemsPerPage: storedFilters?.ItemsPerPage ?? DEFAULT_ITEMS_PER_PAGE,
      Page: storedFilters?.Page ?? DEFAULT_PAGE,
    };
  }, [storedFilters]);

  const {
    pageNumber,
    rowsPerPage,
    totalRowsCount,
    change: changePagination,
  } = usePagination(warehousesData?.CountItems || 0);

  const searchWarehousesHandler = useCallback(
    async (data: ModelWarehouseFilter) => {
      if (!IsOn) return;

      try {
        const result = await searchWarehouses({
          ActiveBranchId: activeBranchId,
          data,
        }).unwrap();

        setWarehousesData(result);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [activeBranchId, searchWarehouses, showNotification, IsOn]
  );

  const getSelectedBranchIds = useCallback(() => {
    return selectedBranches.length > 0
      ? selectedBranches.map((item) => Number(item.value))
      : null;
  }, [selectedBranches]);

  const createSearchPayload = useCallback(
    ({
      page,
      pageSize,
      searchTerm,
    }: {
      page: number;
      pageSize: number;
      searchTerm?: string | null;
    }): ModelWarehouseFilter => {
      const normalizedSearchTerm = searchTerm?.trim();

      return {
        SearchTerm:
          normalizedSearchTerm && normalizedSearchTerm.length > 0
            ? normalizedSearchTerm
            : null,
        BranchIds: getSelectedBranchIds(),
        ItemsPerPage: pageSize,
        Page: page,
      };
    },
    [getSelectedBranchIds]
  );

  useEffect(() => {
    if (!storedFilters?.BranchIds?.length) return;
    if (!allBranches.length) return;

    const restored = allBranches.filter((opt) =>
      storedFilters.BranchIds!.includes(Number(opt.value))
    );

    setSelectedBranches(restored);
  }, [allBranches, storedFilters]);

  useEffect(() => {
    filtersForm.setFieldsValue({
      SearchTerm: initialFilterPayload.SearchTerm ?? undefined,
    });

    changePagination(
      initialFilterPayload.Page,
      initialFilterPayload.ItemsPerPage
    );
  }, [changePagination, filtersForm, initialFilterPayload]);

  const handleChangePagination = useCallback(
    (page: number, pageSize: number) => {
      changePagination(page, pageSize);

      const payload = createSearchPayload({
        page,
        pageSize,
        searchTerm: filtersForm.getFieldValue('SearchTerm') ?? null,
      });

      writeStorage({
        SearchTerm: payload.SearchTerm,
        BranchIds: payload.BranchIds,
        ItemsPerPage: payload.ItemsPerPage,
        Page: payload.Page,
      });

      searchWarehousesHandler(payload);
    },
    [
      changePagination,
      createSearchPayload,
      filtersForm,
      searchWarehousesHandler,
    ]
  );

  const handleSearch = useCallback(
    (values: FiltersForm) => {
      const nextPage = 1;

      changePagination(nextPage, rowsPerPage);

      const payload = createSearchPayload({
        page: nextPage,
        pageSize: rowsPerPage,
        searchTerm: values.SearchTerm ?? null,
      });

      writeStorage({
        SearchTerm: payload.SearchTerm,
        BranchIds: payload.BranchIds,
        ItemsPerPage: payload.ItemsPerPage,
        Page: payload.Page,
      });

      searchWarehousesHandler(payload);
    },
    [
      changePagination,
      createSearchPayload,
      rowsPerPage,
      searchWarehousesHandler,
    ]
  );

  const resetSearch = useCallback(() => {
    filtersForm.resetFields();
    setSelectedBranches([]);

    changePagination(DEFAULT_PAGE, DEFAULT_ITEMS_PER_PAGE);
    clearStorage();

    searchWarehousesHandler({
      SearchTerm: null,
      BranchIds: null,
      ItemsPerPage: DEFAULT_ITEMS_PER_PAGE,
      Page: DEFAULT_PAGE,
    });
  }, [changePagination, filtersForm, searchWarehousesHandler]);

  const getBranchesForSystemHandler = useCallback(async () => {
    if (!IsOn) return;

    try {
      const branchesData = await getBranchesForSystem({
        ActiveBranchId: activeBranchId,
      }).unwrap();
      const adaptedBranches = branchesData.map(({ Id, Name }) => {
        return {
          value: Id,
          label: Name,
        };
      });
      setAllBranches(adaptedBranches);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [IsOn, getBranchesForSystem, activeBranchId, showNotification]);

  const tableColumns = useMemo(() => {
    return IsWarehouseEdit
      ? WAREHOUSE_REGISTRY_COLUMNS
      : WAREHOUSE_REGISTRY_COLUMNS.filter((column) => column.key !== U_ACTION);
  }, [IsWarehouseEdit]);

  const tableData = useMemo(() => {
    return adaptWareHousesToTable(
      warehousesData?.Warehouses || [],
      branches || [],
      IsWarehouseEdit
    );
  }, [warehousesData?.Warehouses, branches, IsWarehouseEdit]);

  const isLoading = useMemo(() => {
    return isSearchWarehousesLoading || isBranchesLoading || isBranchesFetching;
  }, [isSearchWarehousesLoading, isBranchesLoading, isBranchesFetching]);

  return {
    filtersForm,
    selectedBranches,
    setSelectedBranches,
    isLoading,
    pageNumber,
    rowsPerPage,
    totalRowsCount,
    handleChangePagination,
    allBranches,
    handleSearch,
    resetSearch,
    tableData,
    tableColumns,
    isMobile,
    initialFilterPayload,
    searchWarehousesHandler,
    getBranchesForSystemHandler,
    IsWarehouseEdit,
    isPageForbidden,
  };
};
